import {
  BuildingIcon,
  CreditCardIcon,
  FileTextIcon,
  MapPinIcon,
  NetworkIcon,
  UserIcon,
} from 'lucide-react'
import { type ReactNode, useEffect, useMemo, useState } from 'react'
import {
  type GetNodeNeighborhoodArgs,
  type GetNodeNeighborhoodResult,
  type NeighborRef,
  primaryLabel,
  sortedGroupEntries,
} from '@/features/agent/tools/get-node-neighborhood'
import { useToolCardOpen } from '@/features/agent/tools/useToolCardOpen'
import { cn } from '@/utils/cn'
import { ToolCallCard, ToolCallHeader, type ToolCallStatus } from './_shared/ToolCallHeader'

export interface GetNodeNeighborhoodViewProps {
  args: GetNodeNeighborhoodArgs
  parsed: GetNodeNeighborhoodResult | null
  running: boolean
  errored: boolean
  /** Resolved action footer, supplied by the connected tool. Pure stories
   *  pass a mock `<ToolCardActions>` here to render the footer. */
  actions?: ReactNode
  /** Force the card expanded even with no body. Defaults to false; the card
   *  auto-expands when it has results and stays collapsed for empty or failed
   *  calls. Stories pass true to render the expanded body. */
  defaultOpen?: boolean
}

const LABEL_ICON: Record<string, typeof UserIcon> = {
  Person: UserIcon,
  Company: BuildingIcon,
  BankAccount: CreditCardIcon,
  Address: MapPinIcon,
  Document: FileTextIcon,
}

/** Pure presentation for a `get_node_neighborhood` tool-result card. */
export function GetNodeNeighborhoodView({
  args,
  parsed,
  running,
  errored,
  actions,
  defaultOpen = false,
}: GetNodeNeighborhoodViewProps) {
  const groups = parsed?.neighbors_by_label ?? {}
  const groupEntries = useMemo(() => sortedGroupEntries(parsed), [parsed])

  const [activeLabel, setActiveLabel] = useState<string | null>(null)
  // Auto-select the first (largest) group when the result lands.
  useEffect(() => {
    if (activeLabel === null && groupEntries.length > 0) {
      setActiveLabel(groupEntries[0][0])
    }
  }, [groupEntries, activeLabel])

  const centerName = parsed?.center?.name ?? 'node'
  const hops = parsed?.hops ?? args.hops ?? 1
  const count = parsed?.neighbor_count ?? 0
  const hasBody = !running && (errored || parsed?.status === 'ok')
  const { open, onToggle } = useToolCardOpen(hasBody, defaultOpen, errored)

  if (!running && parsed?.status !== 'ok' && !errored) return null

  const headerStatus: ToolCallStatus = running ? 'running' : errored ? 'error' : 'complete'
  const description = running ? (
    `Exploring (${hops} hop${hops === 1 ? '' : 's'})`
  ) : errored ? (
    'Failed'
  ) : parsed?.center ? (
    <>
      {count} neighbour{count === 1 ? '' : 's'}
      <span className="mx-1.5">·</span>
      <span className="text-foreground">{centerName}</span>
      <span className="mx-1.5">·</span>
      {hops === 1 ? '1 hop' : `${hops} hops`}
    </>
  ) : (
    'Neighbours'
  )

  const activeEntries = activeLabel && groups[activeLabel] ? groups[activeLabel] : []

  return (
    <ToolCallCard>
      <ToolCallHeader
        status={headerStatus}
        icon={NetworkIcon}
        toolName="get_node_neighborhood"
        description={description}
        expandable={hasBody}
        open={open}
        onToggle={onToggle}
      />

      {open && hasBody && (
        <div className="border-t border-border">
          {parsed?.status === 'error' && (
            <p className="whitespace-pre-wrap p-3 text-xs text-muted-foreground">
              {parsed.error ?? 'Unknown error'}
            </p>
          )}

          {parsed?.status === 'ok' && (
            <div className="flex flex-col">
              {/* Type pills */}
              <div className="flex flex-wrap gap-1.5 border-b border-border bg-muted/30 px-3 py-2">
                {groupEntries.map(([label, entities]) => {
                  const Icon = LABEL_ICON[label] ?? UserIcon
                  const isActive = label === activeLabel
                  return (
                    <button
                      key={label}
                      type="button"
                      onClick={() => setActiveLabel(label)}
                      className={cn(
                        'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] transition max-md:px-2.5 max-md:py-1.5 max-md:text-xs',
                        isActive
                          ? 'border-primary/60 bg-primary/10 text-foreground'
                          : 'border-border bg-background text-muted-foreground hover:bg-muted'
                      )}
                    >
                      <Icon className="size-3" />
                      <span>{label}</span>
                      <span
                        className={cn(
                          'ml-0.5 rounded px-1 text-[10px] font-semibold',
                          isActive
                            ? 'bg-primary/20 text-foreground'
                            : 'bg-muted text-muted-foreground'
                        )}
                      >
                        {entities.length}
                      </span>
                    </button>
                  )
                })}
              </div>

              {/* Filtered list */}
              <NeighborList entries={activeEntries} />

              {count > 0 && actions && (
                <div className="border-t border-border px-3 py-2">{actions}</div>
              )}
            </div>
          )}
        </div>
      )}
    </ToolCallCard>
  )
}

function NeighborList({ entries }: { entries: NeighborRef[] }) {
  return (
    <ul className="divide-y divide-border">
      {entries.map(n => {
        const label = primaryLabel(n.labels)
        return (
          <li key={n.element_id} className="flex items-start gap-2.5 px-3 py-2 text-xs">
            <span className="mt-1.5 size-1.5 shrink-0 rounded-full bg-primary" />
            <div className="min-w-0 flex-1">
              <p className="truncate font-medium text-foreground">{n.name}</p>
              {n.subtitle && (
                <p className="truncate text-[11px] text-muted-foreground">{n.subtitle}</p>
              )}
            </div>
            {label && (
              <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
                {label}
              </span>
            )}
          </li>
        )
      })}
    </ul>
  )
}
